import { activeBeat, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  layoutLocationBoxes,
  type LocationLayoutView,
} from "../../location/LocationLayout";
import { locationStackStagePosition } from "../../location/LocationMarker/stagePosition";
import { partLabelOffsetsForStack } from "../../location/LocationPartLabels/stackPartLabels";
import { allLocationRuns } from "../../location/LocationRun";
import {
  activeLocationRun,
  buildLocationRuns,
  locationPathOf,
} from "../../location/LocationRun";

export interface InstantPartSnapshot {
  placeId: string;
  label: string;
  dx: number;
}

export interface InstantLocationSnapshot {
  branchId: string;
  anchorX: number;
  topY: number;
  siteDx: number;
  zIndex: number;
  parts: InstantPartSnapshot[];
}

export interface BoardInstantSnapshot {
  playheadMs: number;
  locations: InstantLocationSnapshot[];
}

/** Well past what any screen shows. A box far from its own time cannot be seen or matter. */
const RUN_NEAR_MS = 60_000;

function runIsNear(run: { startMs: number; endMs: number }, playheadMs: number): boolean {
  return playheadMs >= run.startMs - RUN_NEAR_MS && playheadMs <= run.endMs + RUN_NEAR_MS;
}

export function snapshotBoardInstant(
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
  view: LocationLayoutView,
): BoardInstantSnapshot {
  const beat = activeBeat(layout, playheadMs);
  const runs = allLocationRuns(layout);
  const sceneRuns = buildLocationRuns(layout.beats);
  const placements = layoutLocationBoxes(
    layout.beats,
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
    view,
    runs,
  );
  const byRunId = new Map(placements.map((p) => [p.runId, p]));
  const activeRun = activeLocationRun(sceneRuns, playheadMs);
  const activePath = beat !== undefined ? locationPathOf(beat) : [];
  const locations: InstantLocationSnapshot[] = [];
  for (const run of runs) {
    const placement = byRunId.get(run.runId);
    if (placement === undefined || !runIsNear(run, playheadMs)) {
      continue;
    }
    const stage = locationStackStagePosition(
      layout,
      run,
      placement,
      playheadMs,
      viewportWidth,
    );
    const siteDx = 0;
    const parts = partLabelOffsetsForStack(
      run,
      layout,
      playheadMs,
      activePath,
      activeRun?.runId,
    ).map((part) => ({
      placeId: part.label,
      label: part.label,
      dx: part.dx,
    }));
    locations.push({
      branchId: run.runId,
      anchorX: stage.anchorX,
      topY: stage.topY,
      siteDx,
      zIndex: placement.zIndex,
      parts,
    });
  }

  return {
    playheadMs,
    locations,
  };
}
